import { useMemo } from 'react';

import { ISurveyAnalyticsFilter } from './analyticsModel';
import { useSurveyAnalytics } from './useSurveyAnalytics';
import { useSurveys } from './useSurveys';
import { useGetCoursesForTeacher } from '../Teachers/GetTeachers';

export interface IFilterOption {
  value: string;
  label: string;
}

export interface ISurveyFilterOptions {
  courses: IFilterOption[];
  groups: IFilterOption[];
  teachers: IFilterOption[];
  surveys: IFilterOption[];
  /**
   * id → human name, keyed BY FILTER FIELD. Deliberately not one flat map:
   * ids are unique only within their entity, so course 8 and survey 8 are
   * different things — a flat map let a survey title render under «Курс».
   */
  labels: Record<string, Record<string, string>>;
  surveysDenied: boolean;
}

/**
 * One source of filter options AND of id→name labels.
 *
 * Both the filter panel and the crumbs need this, and they must agree: the
 * crumbs first tried to resolve names from the rows currently on screen, so
 * after «drill into a course, switch to groups» the course crumb fell back
 * to a raw id — the very thing the crumbs exist to prevent.
 *
 * Apollo dedupes the identical queries, so calling this from two components
 * costs one round trip.
 *
 * Group and teacher options come from the analytics itself rather than a
 * directory: the only groups worth offering are ones that HAVE survey data,
 * and it adds no permission surface the page does not already have.
 */
export const useSurveyFilterOptions = (
  filter: ISurveyAnalyticsFilter,
): ISurveyFilterOptions => {
  const { data: courses } = useGetCoursesForTeacher();
  const { surveys, error: surveysError } = useSurveys({});

  // Narrowed by the chosen course so the group list stays usable, but NOT by
  // the group/teacher filters themselves — a picker that hides everything you
  // have not already picked cannot be used to pick a second one.
  const scope = useMemo(() => ({ courseIds: filter.courseIds }), [
    filter.courseIds,
  ]);
  const { rows: groupRows } = useSurveyAnalytics('group', scope);
  const { rows: teacherRows } = useSurveyAnalytics('teacher', scope);

  return useMemo(() => {
    const courseOptions = (courses || []).map((course) => ({
      value: course.id,
      label: course.name,
    }));
    const groupOptions = groupRows
      .filter((row) => row.key)
      .map((row) => ({ value: row.key as string, label: row.label }));
    const teacherOptions = teacherRows
      .filter((row) => row.key)
      .map((row) => ({ value: row.key as string, label: row.label }));
    const surveyOptions = surveys.map((survey) => ({
      value: survey.id,
      label: survey.title,
    }));

    const toMap = (options: IFilterOption[]) =>
      Object.fromEntries(options.map((option) => [option.value, option.label]));
    const labels = {
      courseIds: toMap(courseOptions),
      groupIds: toMap(groupOptions),
      teacherIds: toMap(teacherOptions),
      surveyIds: toMap(surveyOptions),
    };

    return {
      courses: courseOptions,
      groups: groupOptions,
      teachers: teacherOptions,
      surveys: surveyOptions,
      labels,
      surveysDenied: Boolean(surveysError),
    };
  }, [courses, groupRows, teacherRows, surveys, surveysError]);
};
